import { toIntlLocale } from 'shared/helpers/intlLocale';

/**
 * SiteUP CRM: helper puro do "proximo passo" do card do funil.
 *
 * Recebe o `next_step` que a API anexa a cada card (ou null) e o instante atual,
 * e devolve o que a faixa do card precisa pra renderizar. Nao toca em i18n, nao
 * usa store e nao depende do Vue: so entra dado, so sai dado.
 *
 * Chaves i18n PRETENDIDAS (a fatia de UI deve cria-las em en e pt_BR; aqui elas
 * sao apenas devolvidas como string):
 *   KANBAN.NEXT_STEP.TASK            "Tarefa · {when}"
 *   KANBAN.NEXT_STEP.TASK_LATE       "Tarefa atrasada · {when}"
 *   KANBAN.NEXT_STEP.APPOINTMENT     "Retorno agendado · {when}"
 *   KANBAN.NEXT_STEP.APPOINTMENT_LATE"Retorno atrasado · {when}"
 *   KANBAN.NEXT_STEP.CARD_SCHEDULE   "Agendado · {when}"
 *   KANBAN.NEXT_STEP.CARD_SCHEDULE_LATE "Agendamento atrasado · {when}"
 *   KANBAN.NEXT_STEP.EMPTY           "Definir proximo passo"
 */

export const NEXT_STEP_LABEL_KEYS = {
  task: 'KANBAN.NEXT_STEP.TASK',
  taskLate: 'KANBAN.NEXT_STEP.TASK_LATE',
  appointment: 'KANBAN.NEXT_STEP.APPOINTMENT',
  appointmentLate: 'KANBAN.NEXT_STEP.APPOINTMENT_LATE',
  cardSchedule: 'KANBAN.NEXT_STEP.CARD_SCHEDULE',
  cardScheduleLate: 'KANBAN.NEXT_STEP.CARD_SCHEDULE_LATE',
  empty: 'KANBAN.NEXT_STEP.EMPTY',
};

const KIND_LABEL_KEYS = {
  task: [NEXT_STEP_LABEL_KEYS.task, NEXT_STEP_LABEL_KEYS.taskLate],
  appointment: [
    NEXT_STEP_LABEL_KEYS.appointment,
    NEXT_STEP_LABEL_KEYS.appointmentLate,
  ],
  card_schedule: [
    NEXT_STEP_LABEL_KEYS.cardSchedule,
    NEXT_STEP_LABEL_KEYS.cardScheduleLate,
  ],
};

// Todas as classes abaixo ja sao usadas em outros pontos do dashboard, entao o
// Tailwind/iconify garantidamente as gera.
const KIND_ICONS = {
  task: 'i-lucide-circle-check',
  appointment: 'i-lucide-calendar-clock',
  card_schedule: 'i-lucide-clock',
};

// Atrasado troca o icone do tipo pelo alerta: a cor sozinha nao serve de sinal
// para quem nao distingue verde de vermelho.
const LATE_ICON = 'i-lucide-triangle-alert';

const EMPTY_RESULT = {
  tone: 'none',
  icon: 'i-lucide-plus',
  labelKey: NEXT_STEP_LABEL_KEYS.empty,
  labelParams: {},
  whenText: '',
};

const MS_PER_DAY = 86400000;

const toDate = value => {
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (value === null || value === undefined || value === '') return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfDay = date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const dayDelta = (at, now) =>
  Math.round((startOfDay(at) - startOfDay(now)) / MS_PER_DAY);

const formatTime = (at, locale) =>
  new Intl.DateTimeFormat(toIntlLocale(locale), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(at);

const formatShortDate = (at, locale, withYear) =>
  new Intl.DateTimeFormat(toIntlLocale(locale), {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
  }).format(at);

/**
 * Texto curto do "quando": hoje e amanha viram so a hora com prefixo relativo
 * (via Intl.RelativeTimeFormat, sem biblioteca nova); o resto vira data curta
 * mais hora, com ano apenas quando for outro ano.
 */
export const formatNextStepWhen = (at, now, locale = 'pt-BR') => {
  const delta = dayDelta(at, now);
  const time = formatTime(at, locale);

  if (delta === 0 || delta === 1 || delta === -1) {
    const relative = new Intl.RelativeTimeFormat(toIntlLocale(locale), {
      numeric: 'auto',
    }).format(delta, 'day');
    return `${relative}, ${time}`;
  }

  const withYear = at.getFullYear() !== now.getFullYear();
  return `${formatShortDate(at, locale, withYear)}, ${time}`;
};

/**
 * @param {Object|null} nextStep payload `next_step` da API.
 * @param {Date|string|number} now instante de referencia.
 * @param {string} locale 'pt-BR' (default) ou 'en'.
 * @returns {{tone: 'ok'|'late'|'none', icon: string, labelKey: string, labelParams: Object, whenText: string}}
 */
export const buildNextStepBadge = (nextStep, now, locale = 'pt-BR') => {
  const reference = toDate(now) || new Date();
  if (!nextStep || typeof nextStep !== 'object') return { ...EMPTY_RESULT };

  const at = toDate(nextStep.at);
  const keys = KIND_LABEL_KEYS[nextStep.kind];
  if (!at || !keys) return { ...EMPTY_RESULT };

  // `overdue` vem do servidor (relogio unico); `now` so decide quando o servidor
  // nao informou.
  const isLate =
    typeof nextStep.overdue === 'boolean'
      ? nextStep.overdue
      : at.getTime() < reference.getTime();

  const whenText = formatNextStepWhen(at, reference, locale);

  return {
    tone: isLate ? 'late' : 'ok',
    icon: isLate ? LATE_ICON : KIND_ICONS[nextStep.kind],
    labelKey: isLate ? keys[1] : keys[0],
    labelParams: {
      when: whenText,
      title: nextStep.title || '',
      assignee: nextStep.assignee_name || '',
    },
    whenText,
  };
};

/**
 * Aba do painel lateral que resolve cada tipo de proximo passo.
 * Os ids sao os de KANBAN_DETAIL_TAB_IDS (KanbanItemDetailView.vue): 'tasks' e
 * a aba Tarefas e 'activities' e a aba Atividades, onde vivem retorno e
 * compromisso do card. Nao existe aba 'agenda' no painel.
 */
const KIND_PANEL_TABS = {
  task: 'tasks',
  appointment: 'activities',
  card_schedule: 'activities',
};

/**
 * Aba que o clique na faixa deve abrir. Sem proximo passo o destino e Tarefas,
 * porque criar a tarefa e o caminho mais curto para definir um.
 * @param {Object|null} nextStep payload `next_step` da API.
 * @returns {string} id de aba do painel.
 */
export const nextStepPanelTab = nextStep =>
  KIND_PANEL_TABS[nextStep?.kind] || 'tasks';

/**
 * Um card está "sem próximo passo"? Só quando a resposta do servidor diz que
 * não tem: `next_step` null. Card compartilhado não recebe o campo, e
 * `undefined` ali significa desconhecido, não ausente.
 */
export function isWithoutNextStep(item) {
  if (!item || typeof item !== 'object') return false;
  if (!('next_step' in item)) return false;
  return item.next_step === null;
}

/**
 * Um card está com o próximo passo atrasado?
 */
export function isOverdueNextStep(item) {
  const nextStep = item?.next_step;
  if (!nextStep || typeof nextStep !== 'object') return false;
  if (!KIND_LABEL_KEYS[nextStep.kind]) return false;
  return nextStep.overdue === true;
}

/**
 * Quantos cards estao com o proximo passo atrasado. Serve o filtro rapido
 * "atrasados" da barra do board, que e de outra fatia: aqui fica so a contagem
 * pura, sem store e sem Vue.
 *
 * Usa `next_step.overdue` do servidor (relogio unico). Item sem `next_step`,
 * com `next_step` malformado ou compartilhado (que nao recebe o campo) nao
 * conta.
 *
 * @param {Array} items lista de itens do Kanban.
 * @returns {number}
 */
export const countOverdue = items => {
  if (!Array.isArray(items)) return 0;
  return items.reduce(
    (total, item) => (isOverdueNextStep(item) ? total + 1 : total),
    0
  );
};

/**
 * Quantos cards estao SEM proximo passo definido.
 *
 * Só conta quem tem a resposta do servidor e ela é "não tem": `next_step`
 * null. Card compartilhado não recebe o campo, e `undefined` ali significa
 * desconhecido, não ausente: contar seria acusar de não ter próximo passo
 * quem talvez tenha.
 *
 * @param {Array} items lista de itens do Kanban.
 * @returns {number}
 */
export const countWithoutNextStep = items => {
  if (!Array.isArray(items)) return 0;
  return items.reduce(
    (total, item) => (isWithoutNextStep(item) ? total + 1 : total),
    0
  );
};

/**
 * Aplica os filtros rápidos da barra sobre os itens já carregados. Os dois
 * ligados juntos somam (ou atrasado ou sem próximo passo), porque a leitura
 * natural dos chips é "me mostre o que precisa de mim".
 *
 * @param {Array} items
 * @param {{overdue?: boolean, withoutNextStep?: boolean}} filters
 * @returns {Array}
 */
export const filterByNextStep = (items, filters) => {
  if (!Array.isArray(items)) return [];
  const overdue = Boolean(filters?.overdue);
  const without = Boolean(filters?.withoutNextStep);
  if (!overdue && !without) return items;
  return items.filter(
    item =>
      (overdue && isOverdueNextStep(item)) ||
      (without && isWithoutNextStep(item))
  );
};

export default buildNextStepBadge;
